import type { ComponentPropsWithoutRef, FC, MouseEvent, ReactNode } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useLenis } from 'lenis/react'
import { goToHash, scrollToId } from '@/lib/nav'

interface SmartLinkProps extends Omit<ComponentPropsWithoutRef<'a'>, 'href'> {
    href: string
    // Runs after a click is handled, for example to close the mobile menu.
    onNavigate?: () => void
}

const isModifiedClick = (event: MouseEvent<HTMLAnchorElement>): boolean =>
    event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0

// One link for the whole site: smooth in-page scrolling on the home page,
// normal navigation everywhere else, and plain anchors for email and other sites.
const SmartLink: FC<SmartLinkProps> = ({ href, onNavigate, onClick, children, ...props }): ReactNode => {
    const router = useRouter()
    const lenis = useLenis()

    if (href.startsWith('mailto:') || href.startsWith('http')) {
        const external = href.startsWith('http')

        return (
            <a
                href={href}
                target={external ? '_blank' : undefined}
                rel={external ? 'noopener noreferrer' : undefined}
                onClick={event => {
                    onClick?.(event)
                    onNavigate?.()
                }}
                {...props}
            >
                {children}
            </a>
        )
    }

    const handleClick = (event: MouseEvent<HTMLAnchorElement>): void => {
        onClick?.(event)

        if (event.defaultPrevented || isModifiedClick(event)) return

        onNavigate?.()

        // A hash on the current page, such as "#routine": scroll there softly instead of jumping.
        if (href.startsWith('#')) {
            event.preventDefault()
            scrollToId(href.slice(1), lenis)
            return
        }

        // A hash on this inner page written as a full path, such as "/about#contact" while on /about.
        const hashIndex = href.indexOf('#')

        if (router.pathname !== '/' && hashIndex > 0 && href.slice(0, hashIndex) === router.pathname) {
            event.preventDefault()
            scrollToId(href.slice(hashIndex + 1), lenis)
            return
        }

        if (router.pathname !== '/') return

        if (href === '/') {
            event.preventDefault()

            if (lenis) lenis.scrollTo(0, { duration: 1.6 })
            else window.scrollTo({ top: 0, behavior: 'smooth' })

            return
        }

        if (href.startsWith('/#')) {
            event.preventDefault()
            goToHash(href.slice(1), lenis)
        }
    }

    return (
        <Link href={href} onClick={handleClick} {...props}>
            {children}
        </Link>
    )
}

export default SmartLink
